import { useCallback, useState } from 'react';

import { getAllBankName } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { colorTokens } from '@revenuemarket/design';
import { useSteps, Steps } from '@revenuemarket/react';
import { Modal, ModalProps, Typography } from 'antd';

import {
  KbWiringResultCode,
  WIRING_RESULT_STATUS,
  WiringResultItem,
  WiringType,
} from '~apis/wiring';
import { APP_ENV } from '~constants/env';
import { useGetWiringAccountList } from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';

import WiringBankConnectStep from './WiringBankConnectStep';
import WiringComplete from './WiringComplete';
import WiringDepositInfoStep from './WiringDepositInfoStep';
import WithdrawalSelectStep from './WithdrawalSelectStep';
import {
  CompressInfo,
  ReservationInfo,
  WireListItem,
  WiringDepositInfoModel,
  WiringSelectModel,
  WiringBankConnectModel,
} from './types';
type StepModel = [WiringSelectModel, WiringDepositInfoModel, WiringBankConnectModel];

interface Props extends ModalProps {
  title?: string;
  payrollMode?: boolean;
  defaultWithdrawalAccountId?: number;
  defaultDepositAccounts?: WireListItem[];
  defaultScheduledInfo?: ReservationInfo;
  defaultCompressInfo?: CompressInfo;
  defaultTitle?: string;
  onSuccess?: () => void;
}

const WiringFlowModal: React.FC<Props> = ({
  title = '이체',
  payrollMode,
  defaultWithdrawalAccountId,
  defaultDepositAccounts = [],
  defaultTitle,
  onSuccess,
  ...props
}) => {
  const [stepModel, moveStep, resetStep] = useSteps<StepModel>({
    name: 'wiringSteps',
  });
  const [wiringType, setWringType] = useState<WiringType>();
  const [isCompleted, setIsCompleted] = useState(false);
  const [wiringId, setWiringId] = useState<number | null>(null);
  const [resultItems, setResultItems] = useState<WiringResultItem[]>([]);
  const [status, setStatus] = useState<WIRING_RESULT_STATUS>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const { companyId } = useCompanyId();
  const { data: accountListData } = useGetWiringAccountList({ companyId });
  const accountList = accountListData?.Data?.data?.items || [];

  const selectedAccount = accountList.find(
    (account) => account.accountId === stepModel?.model?.accountInfoId,
  );

  const getSubTitle = () => {
    if (resultItems?.length > 1) {
      if (stepModel.model?.wiringBank === 'KB') {
        return '';
      }
      return '대량이체로 등록했어요.';
    }

    if (payrollMode) {
      return '급여이체로 등록했어요.';
    }
    return '';
  };

  const handleError = useCallback(
    ({ errorMessage, errorCode }: { errorMessage: string; errorCode: string }) => {
      setErrorMessage(errorMessage);
      const isProd = APP_ENV === 'prod';

      switch (errorCode) {
        case KbWiringResultCode.UBFN0197:
        case KbWiringResultCode.UBFN0270:
          window.open(
            isProd
              ? `https://obizapi.kbstar.com/quics?page=C108078&redirect=Y`
              : `https://zobiz.kbstar.com/quics?page=C108078&redirect=Y`,
            '_blank',
            'noopener,noreferrer',
          );
          break;
        case KbWiringResultCode.UBFN0271:
        case KbWiringResultCode.UBFN0272:
        case KbWiringResultCode.UBFN0273:
          window.open(
            isProd
              ? `https://obizapi.kbstar.com/quics?page=C108016&redirect=Y`
              : `https://zobiz.kbstar.com/quics?page=C108016&redirect=Y`,
            '_blank',
            'noopener,noreferrer',
          );
          break;
        default:
          break;
      }
    },
    [],
  );

  return (
    <Modal
      width={'90vw'}
      {...props}
      maskClosable={false}
      centered
      title={isCompleted ? '' : title}
      bodyStyle={{
        paddingTop: 24,
        maxHeight: 'calc(100vh - 100px)',
      }}
      footer={null}
      onCancel={(e) => {
        resetStep();
        props.onCancel?.(e);
      }}
    >
      {isCompleted && wiringId ? (
        <WiringComplete
          resultItems={resultItems}
          subTitle={getSubTitle()}
          wiringId={wiringId}
          wiringType={wiringType}
          onCancel={(e) => props.onCancel?.(e)}
          model={stepModel.model as StepModel[2]}
          defaultStatus={status}
          onSuccess={onSuccess}
          onError={handleError}
          errorMessage={errorMessage}
        />
      ) : (
        <Steps
          stepModel={stepModel}
          stepDefinitions={[
            {
              title: (
                <Flex align="center" gap={4}>
                  <Typography.Text>출금 계좌 선택</Typography.Text>
                  {selectedAccount ? (
                    <Typography.Text
                      style={{
                        color: colorTokens.textPrimary,
                      }}
                    >{`
                ${selectedAccount?.aliasName} (${getAllBankName(
                  selectedAccount?.bankCode,
                  true,
                )} ${selectedAccount?.accountNumber.slice(-4)}) 
                `}</Typography.Text>
                  ) : (
                    <div />
                  )}
                </Flex>
              ),
            },
            {
              title: '입금 정보 입력',
            },
            {
              title: '은행 연결',
            },
          ]}
        >
          <Steps.Item<StepModel, 0> step={0}>
            {(model) => (
              <WithdrawalSelectStep
                defaultCompressInfo={props.defaultCompressInfo}
                defaultScheduledInfo={props.defaultScheduledInfo}
                defaultWithdrawalAccountId={defaultWithdrawalAccountId}
                payrollMode={payrollMode ?? false}
                model={model}
                onFinish={(model) =>
                  moveStep({
                    step: 1,
                    model,
                  })
                }
              />
            )}
          </Steps.Item>
          <Steps.Item<StepModel, 1> step={1}>
            {(model) => (
              <WiringDepositInfoStep
                defaultTitle={defaultTitle}
                model={model}
                defaultDepositInfo={defaultDepositAccounts}
                onBack={(model: WiringDepositInfoModel) => {
                  moveStep({ step: 0, model: { ...model } });
                }}
                onFinish={(model) => {
                  moveStep({ step: 2, model });
                }}
              />
            )}
          </Steps.Item>
          <Steps.Item<StepModel, 2> step={2}>
            {(model) => (
              <WiringBankConnectStep
                wiringId={wiringId}
                setWiringId={setWiringId}
                model={model}
                onFinish={(data) => {
                  const items = data.items;
                  setWringType(data.wiringType);
                  setStatus(data.status);
                  setResultItems(items);
                  setIsCompleted(true);
                  if (data.code && data.message) {
                    handleError({ errorMessage: data.message, errorCode: data.code });
                  }
                }}
                onBack={() => {
                  moveStep({ step: 1, model });
                }}
              />
            )}
          </Steps.Item>
        </Steps>
      )}
    </Modal>
  );
};

export default WiringFlowModal;
